/**
 * Shared PermissionGate + useHasPerm hook.
 *
 * Every workflow-permission-gated UI in the app funnels through here so we
 * have exactly ONE decision point. Backend uses `require_workflow_permission`
 * to enforce the same list independently — this component only controls
 * visibility.
 *
 * Usage:
 *   <PermissionGate perm="ORDER">
 *     <NavLink to="/orders">Book Order</NavLink>
 *   </PermissionGate>
 *
 *   const canOrder = useHasPerm("ORDER");
 */
import { useAuth } from "@/lib/auth";

/** Returns true iff the current user has the given workflow permission code. */
export function useHasPerm(code) {
  const { hasWorkflowPerm } = useAuth();
  if (!code) return false;
  return hasWorkflowPerm(code);
}

/** Returns true iff the current user has ANY of the given codes. */
export function useHasAnyPerm(codes) {
  const { hasWorkflowPerm } = useAuth();
  if (!Array.isArray(codes) || codes.length === 0) return false;
  return codes.some((c) => hasWorkflowPerm(c));
}

/**
 * Render `children` only when the caller holds `perm`. Optional `fallback`
 * renders when denied (default: nothing). Optional `anyOf=[…]` grants access
 * if ANY of the listed codes is present.
 */
export default function PermissionGate({ perm, anyOf, fallback = null, children }) {
  const { hasWorkflowPerm } = useAuth();
  let ok = false;
  if (perm) ok = hasWorkflowPerm(perm);
  else if (Array.isArray(anyOf) && anyOf.length > 0) {
    ok = anyOf.some((c) => hasWorkflowPerm(c));
  }
  if (!ok) return fallback;
  return children;
}
